// Permission helpers for the admin SPA (PX-73).
//
// The wildcard `*` is the backend sentinel for the built-in admin
// role — anyone holding it satisfies every permission check. Plain
// users / curators / custom roles list concrete scopes; the helpers
// here check that list against the required permissions.

import type { Permission, ProfileResponse } from '@/types/api'

const WILDCARD: Permission = '*'

/** True if `profile` has `perm` (wildcard counts). */
export function hasPerm(profile: ProfileResponse | null | undefined, perm: Permission): boolean {
  if (!profile) return false
  const set = profile.permissions
  if (!Array.isArray(set)) return false
  if (set.includes(WILDCARD)) return true
  return set.includes(perm)
}

/** True if `profile` has ANY of the requested permissions (OR). */
export function hasAnyPerm(profile: ProfileResponse | null | undefined, perms: Permission[]): boolean {
  if (!profile) return false
  if (perms.length === 0) return true
  if (profile.permissions?.includes(WILDCARD)) return true
  return perms.some((p) => profile.permissions.includes(p))
}

/** True if `profile` has ALL of the requested permissions (AND). */
export function hasAllPerms(profile: ProfileResponse | null | undefined, perms: Permission[]): boolean {
  if (!profile) return false
  if (perms.length === 0) return true
  if (profile.permissions?.includes(WILDCARD)) return true
  return perms.every((p) => profile.permissions.includes(p))
}

/** True if `profile` carries any admin-level access at all. The
 *  RequireRole gate (the predecessor in PX-71) used this distinction
 *  to redirect plain users back to /login. */
export function isPrivileged(profile: ProfileResponse | null | undefined): boolean {
  return !!profile && Array.isArray(profile.permissions) && profile.permissions.length > 0
}
